import { useState } from 'react'

import { InfoCircleOutlined, PlusSquareOutlined } from '@ant-design/icons'
import { useSetState } from 'ahooks'
import { Descriptions, Popover, Typography } from 'antd'
import { createStyles } from 'antd-style'
import { isNil } from 'lodash'

import { BidForm, BidResult } from '@eastcoal/api/trade/interface'

import { ConfirmButton } from '@eastcoal/common/components/Button/ConfirmButton'
import { ThrottleButton } from '@eastcoal/common/components/Button/ThrottleButton'
import { toBig } from '@eastcoal/common/utils/decimal'
import { pick } from '@eastcoal/common/utils/object'

import { EntranceType } from '@eastcoal/shared/enums/EntranceType'

import { TradeSessionStatus } from '../../../../../../enums/TradeSessionStatus'
import { useAuctionDetailStore } from '../../../Store/hooks/useStore'
import { formatBidTime } from '../../../utils/formatBidTime'

import { AuctionBidInputNumber } from './InputNumber'

const useStyles = createStyles(({ token, prefixCls }) => ({
  container: {
    position: 'relative'
  },
  form: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr auto',
    gap: token.padding
  },
  formItem: {
    display: 'flex',
    alignItems: 'center'
  },
  label: {
    color: token.colorTextLabel
  },
  descriptions: {
    width: 440,
    [`.${prefixCls}-descriptions-item-label`]: {
      color: token.colorTextBase
    }
  },
  result: {
    height: token.paddingXL,
    paddingInlineStart: '4em',
    fontSize: token.fontSizeSM,
    transition: 'height 0.2s ease-in-out'
  },
  heightLG: {
    height: token.paddingXL + token.paddingXS
  },
  successMessage: {
    color: token.colorSuccessText
  },
  errorMessage: {
    color: token.colorErrorText
  },
  desc: {
    color: token.colorTextTertiary
  }
}))

const addCalc = (valueA: number | string, valueB: number | string) => {
  return toBig(valueA ?? 0)
    .add(toBig(valueB ?? 0))
    .toNumber()
}

export const AuctionDetailBidForm = () => {
  const {
    entranceType,
    detailData,
    bidTip,
    statusInfo,
    isBidOnlyOnce,
    sendBidRequest
  } = useAuctionDetailStore(state =>
    pick(state, [
      'entranceType',
      'detailData',
      'bidTip',
      'statusInfo',
      'isBidOnlyOnce',
      'sendBidRequest'
    ])
  )

  const [formData, setFormData] = useSetState<BidForm>({ count: 1 })

  const { wholeSale, minAddPrice = 0, maxDecimalDigits = 2 } = detailData

  const handlePriceChange = (price?: number) => {
    setFormData({ price })
  }

  const handleCountChange = (count?: number) => {
    setFormData({ count })
  }

  // 最低可出价
  const allowMinPrice = Math.max(bidTip.allowMinPrice ?? 0, bidTip.myPrice ?? 0)

  const handlePricePlus = (inptVal?: number) => {
    // 输入框为空或者当前值小于最低可成交价时，设置为最小可成交价
    if (isNil(inptVal) || inptVal < allowMinPrice) {
      setFormData({ price: allowMinPrice })
      // 输入框不为空时，加上最小加价幅度
    } else {
      setFormData({ price: addCalc(inptVal, minAddPrice) })
    }
  }

  const handleCountPlus = (value = 0) => {
    setFormData({ count: addCalc(value, 1) })
  }

  const [bidResult, setBidResult] = useState<BidForm & BidResult>()

  const forbidBid = isBidOnlyOnce() && !!bidResult?.success
  const inputDisabled =
    statusInfo?.status === TradeSessionStatus.WAIT_BID_START ||
    statusInfo?.status === TradeSessionStatus.BID_END ||
    forbidBid ||
    entranceType !== EntranceType.BID

  const bidBtnDisabled =
    isNil(formData.price) || !(formData.count ?? 0) || inputDisabled

  const messageTitle =
    bidResult && `「 ${bidResult.bidTime} 」${bidResult.message}`
  const messageDesc =
    bidResult && `（价格：${bidResult.price} 数量：${bidResult.count}）`

  const handleBid = async () => {
    // 保留副本，请求之后再获取可能就变了
    const { price, count } = formData
    const { bidTime, message, success } = await sendBidRequest(formData)
    setBidResult({
      price,
      count,
      message,
      success,
      bidTime: formatBidTime(bidTime)
    })
  }

  const canBidMaxCount = bidTip.canBidMaxCount ?? 1

  const { cx, styles } = useStyles()

  const priceDescriptions = (
    <Descriptions
      colon
      className={styles.descriptions}
      column={1}
      layout='horizontal'
      size='small'
      items={[
        {
          label: '价格精度',
          children: (
            <span>
              当前场次规定价格最多&nbsp;
              <Typography.Text type='danger'>
                {maxDecimalDigits}
              </Typography.Text>
              &nbsp;位小数
            </span>
          )
        },
        {
          label: '出价要求',
          children: '输入框为空时出价按钮被禁用，无法出价'
        },
        {
          label: '操作说明',
          children: (
            <span>
              点击右侧的&nbsp;
              <PlusSquareOutlined />
              &nbsp;按钮，如果输入框的值为空或者小于最低可成交价则填入 “
              最低可成交价” ，否则在输入框的基础上加上 “最小加价幅度”
            </span>
          )
        }
      ]}
    />
  )

  return (
    <div className={styles.container}>
      <div className={styles.form}>
        <div className={styles.formItem}>
          <Popover content={priceDescriptions} trigger='hover'>
            <span className={styles.label}>价格</span>
            <InfoCircleOutlined />
            <span>：</span>
          </Popover>
          <AuctionBidInputNumber
            allowClear
            disabled={inputDisabled}
            maxPrecision={maxDecimalDigits}
            min={0}
            value={formData.price}
            onChange={handlePriceChange}
            onPlus={handlePricePlus}
          />
        </div>
        <div className={styles.formItem}>
          <Popover
            content={`出价份数最大值为 ${canBidMaxCount}（报名份数）`}
            trigger='hover'
          >
            <span className={styles.label}>份数</span>
            <InfoCircleOutlined />
            <span>：</span>
          </Popover>
          <AuctionBidInputNumber
            allowClear
            disabled={wholeSale || inputDisabled}
            max={canBidMaxCount}
            maxPrecision={0}
            min={1}
            value={formData.count}
            onChange={handleCountChange}
            onPlus={handleCountPlus}
          />
        </div>
        {isBidOnlyOnce() ? (
          <ConfirmButton
            disabled={bidBtnDisabled}
            forbiddenStrategy='disable'
            message='仅可出价一次，是否确认出价？'
            type='primary'
            onConfirm={handleBid}
          >
            出价
          </ConfirmButton>
        ) : (
          <ThrottleButton
            disabled={bidBtnDisabled}
            throttleTime={300}
            type='primary'
            onClick={handleBid}
          >
            出价
          </ThrottleButton>
        )}
      </div>
      <div
        className={cx(styles.result, {
          [styles.heightLG]: !!bidResult
        })}
      >
        <span
          className={
            bidResult?.success ? styles.successMessage : styles.errorMessage
          }
        >
          {messageTitle}
        </span>
        <span className={styles.desc}>{messageDesc}</span>
      </div>
    </div>
  )
}
